import { MY_PATH_URL } from '../../paths/utils/pathsChatUrl';

/**
 * For tutorial/productivity app icons: show first word only, no underscores.
 * e.g. "Panel Interview (Medicine)" -> "Panel", "medicine_panel_interview" -> "medicine" (or use title "Panel").
 */
export function getTutorialIconLabel(app) {
  if (!app) return 'App';
  const raw = (app.title || app.exam_name || '').toString().trim();
  const noUnderscores = raw.replace(/_/g, ' ');
  const first = noUnderscores.split(/\s+/)[0] || raw;
  return first || 'App';
}

/** Task List is hidden from store/apps grids across all exam portals. */
export function isHiddenTaskListApp(app) {
  if (!app) return false;
  const exam = (app.exam_name || '').toLowerCase().trim();
  const title = (app.title || '').toLowerCase().trim();
  const id = String(app.id || '').toLowerCase();
  return (
    exam === 'task_list' ||
    title === 'task list' ||
    id.includes('task_list') ||
    id.includes('task-list')
  );
}

export const EXAM_PRACTICE_KEYS = ['lnat', 'tmua', 'ucat', 'esat', 'tara', 'lsat', 'step'];

/**
 * Resolve exam portal from hostname first (lnat.edu.ivori.ai → lnat).
 * Falls back to plan / subject only when not on an exam subdomain.
 */
export function resolveHostExamTrack({ hostname, origin, userPlan, userSubject } = {}) {
  const host = String(hostname || (typeof window !== 'undefined' ? window.location.hostname : '') || '').toLowerCase();
  const originStr = String(origin || (typeof window !== 'undefined' ? window.location.origin : '') || '').toLowerCase();
  const plan = String(userPlan || '').toLowerCase();
  const subject = String(userSubject || '').toLowerCase().trim();

  // pathbuddy.com / edu apex: subject personalisation is handled separately in
  // UserPurchasedAppsPage via resolveSubjectAppsTrack — do not narrow here.
  if (host === 'pathbuddy.com' || host === 'www.pathbuddy.com') {
    return null;
  }

  for (const ex of EXAM_PRACTICE_KEYS) {
    if (host.startsWith(`${ex}.`) || host.startsWith(ex) || host.includes(`.${ex}.`) || originStr.includes(`${ex}.`)) {
      return ex;
    }
  }

  for (const ex of EXAM_PRACTICE_KEYS) {
    if (plan.includes(`${ex}_`) || plan.startsWith(ex) || subject === ex) {
      return ex;
    }
  }

  if (subject === 'law') return 'lnat';
  if (subject === 'computer science' || subject === 'math' || subject === 'mathematics') return 'tmua';
  if (subject === 'medicine') return 'ucat';
  if (subject === 'engineering') return 'esat';

  return null;
}

/** True if this tile is an exam-practice app for a different portal than hostExam. */
export function isForeignExamPracticeApp(app, hostExam) {
  if (!app || !hostExam) return false;
  const exam = (app.exam_name || '').toLowerCase().trim();
  const title = (app.title || '').toLowerCase().trim();
  const id = String(app.id || '').toLowerCase();
  const appType = (app.app_type || '').toLowerCase();

  const matchedExam = EXAM_PRACTICE_KEYS.find(
    (ex) =>
      exam === ex ||
      title === ex ||
      title === `${ex} practice` ||
      id === `${ex}_exam_app` ||
      id.startsWith(`${ex}_exam`)
  );

  if (!matchedExam) return false;
  if (appType === 'productivity' || appType === 'utility') return false;
  return matchedExam !== hostExam;
}

/**
 * The three live interview workspaces (Medicine, Dentistry, Oxbridge).
 * Whether a user sees one is decided by what they own (ownedApps.panelsFor),
 * not by subject, host or plan name.
 */
export const LIVE_INTERVIEW_WORKSPACES = ['live_interview_medicine', 'live_interview_dentistry', 'live_interview_oxbridge'];

const LIVE_INTERVIEW_TILES = LIVE_INTERVIEW_WORKSPACES.map((exam_name) => ({ exam_name, category: 'Interview Apps' }));

/**
 * Per-exam catalogue keys (exam_name) — mirrors backend tier_apps_config tracks.
 * Used on lnat.edu / tmua.edu / etc. so each portal only shows its suite.
 */
export const TRACK_ALLOWED_EXAM_NAMES = {
  lnat: ['lnat', 'custom_quiz_generator', 'personal_statement', 'cv', 'cover_letter', 'interview_simulator', ...LIVE_INTERVIEW_WORKSPACES],
  lsat: ['lsat', 'custom_quiz_generator', 'personal_statement', 'cv', 'cover_letter', 'interview_simulator', ...LIVE_INTERVIEW_WORKSPACES],
  tmua: ['tmua', 'custom_quiz_generator', 'personal_statement', 'interview_simulator', ...LIVE_INTERVIEW_WORKSPACES],
  esat: ['esat', 'custom_quiz_generator', 'personal_statement', 'interview_simulator', ...LIVE_INTERVIEW_WORKSPACES],
  step: ['step', 'custom_quiz_generator', 'personal_statement', 'interview_simulator', ...LIVE_INTERVIEW_WORKSPACES],
  ucat: [
    'ucat',
    'custom_quiz_generator',
    'personal_statement',
    'cv',
    'interview_simulator',
    'medical_interviews',
    ...LIVE_INTERVIEW_WORKSPACES,
  ],
  tara: ['tara', 'custom_quiz_generator', 'personal_statement', 'cv', 'cover_letter', 'interview_simulator', ...LIVE_INTERVIEW_WORKSPACES],
};

/** Category rows for pathbuddy.com subject-personalised /apps layouts. */
export const SUBJECT_APPS_CATEGORY_ORDER = [
  'Exam Apps',
  'Interview Apps',
  'Utility Apps',
  'Mastery Apps',
];

const MASTERY_APPS = [
  { exam_name: 'verbal_reasoning', category: 'Mastery Apps' },
  { exam_name: 'numerical_reasoning', category: 'Mastery Apps' },
  { exam_name: 'logical_reasoning', category: 'Mastery Apps' },
  { exam_name: 'pattern_recognition', category: 'Mastery Apps' },
];

const UTILITY_APPS = [
  { exam_name: 'personal_statement', category: 'Utility Apps' },
  { exam_name: 'cover_letter', category: 'Utility Apps' },
  { exam_name: 'cv', category: 'Utility Apps' },
];

/**
 * Ordered catalogue per subject track (exam_name → category).
 * Name aliases map to existing apps (Custom Exam → custom_quiz_generator, etc.).
 * "Custom Interview" is omitted — same tile as Interview Simulator / Workspace.
 */
export const SUBJECT_APPS_BY_TRACK = {
  lnat: [
    { exam_name: 'lnat', category: 'Exam Apps' },
    { exam_name: 'custom_quiz_generator', category: 'Exam Apps' },
    { exam_name: 'interview_simulator', category: 'Interview Apps' },
    ...LIVE_INTERVIEW_TILES,
    { exam_name: 'oxbridge_interview', category: 'Interview Apps' },
    { exam_name: 'scholarship_interview', category: 'Interview Apps' },
    { exam_name: 'law_interview', category: 'Interview Apps' },
    ...UTILITY_APPS,
    ...MASTERY_APPS,
  ],
  tmua: [
    { exam_name: 'tmua', category: 'Exam Apps' },
    { exam_name: 'custom_quiz_generator', category: 'Exam Apps' },
    { exam_name: 'interview_simulator', category: 'Interview Apps' },
    ...LIVE_INTERVIEW_TILES,
    { exam_name: 'oxbridge_interview', category: 'Interview Apps' },
    { exam_name: 'scholarship_interview', category: 'Interview Apps' },
    { exam_name: 'investment_banking_interview', category: 'Interview Apps' },
    { exam_name: 'operations_fs_interview', category: 'Interview Apps' },
    { exam_name: 'private_equity_interview', category: 'Interview Apps' },
    { exam_name: 'computer_science_interview', category: 'Interview Apps' },
    ...UTILITY_APPS,
    ...MASTERY_APPS,
  ],
  tara: [
    { exam_name: 'tara', category: 'Exam Apps' },
    { exam_name: 'custom_quiz_generator', category: 'Exam Apps' },
    { exam_name: 'interview_simulator', category: 'Interview Apps' },
    ...LIVE_INTERVIEW_TILES,
    { exam_name: 'oxbridge_interview', category: 'Interview Apps' },
    { exam_name: 'scholarship_interview', category: 'Interview Apps' },
    // Management Consulting interview app does not exist yet
    { exam_name: 'investment_banking_interview', category: 'Interview Apps' },
    { exam_name: 'private_equity_interview', category: 'Interview Apps' },
    ...UTILITY_APPS,
    ...MASTERY_APPS,
  ],
  ucat: [
    { exam_name: 'ucat', category: 'Exam Apps' },
    { exam_name: 'custom_quiz_generator', category: 'Exam Apps' },
    { exam_name: 'interview_simulator', category: 'Interview Apps' },
    ...LIVE_INTERVIEW_TILES,
    { exam_name: 'oxbridge_interview', category: 'Interview Apps' },
    { exam_name: 'scholarship_interview', category: 'Interview Apps' },
    { exam_name: 'medical_interviews', category: 'Interview Apps' },
    { exam_name: 'medicine_panel_interview', category: 'Interview Apps' },
    { exam_name: 'medicine_mmi_interview', category: 'Interview Apps' },
    ...UTILITY_APPS,
    ...MASTERY_APPS,
  ],
  esat: [
    { exam_name: 'esat', category: 'Exam Apps' },
    { exam_name: 'custom_quiz_generator', category: 'Exam Apps' },
    { exam_name: 'interview_simulator', category: 'Interview Apps' },
    ...LIVE_INTERVIEW_TILES,
    { exam_name: 'oxbridge_interview', category: 'Interview Apps' },
    { exam_name: 'scholarship_interview', category: 'Interview Apps' },
    { exam_name: 'computer_science_interview', category: 'Interview Apps' },
    { exam_name: 'engineering_interview', category: 'Interview Apps' },
    { exam_name: 'investment_banking_interview', category: 'Interview Apps' },
    { exam_name: 'private_equity_interview', category: 'Interview Apps' },
    ...UTILITY_APPS,
    ...MASTERY_APPS,
  ],
};

/**
 * Map Settings primary_subject → subject apps track.
 * Undecided / unmapped subjects return null (show full default catalogue).
 */
export function resolveSubjectAppsTrack(userSubject) {
  const subject = String(userSubject || '').toLowerCase().trim();
  if (!subject || subject === 'undecided' || subject === 'none') return null;

  if (subject === 'law' || subject === 'lnat') return 'lnat';
  // Medicine & Dentistry share the UCAT track (same admissions-test pathway).
  if (subject === 'medicine' || subject === 'dentistry' || subject === 'ucat') return 'ucat';
  if (subject === 'engineering' || subject === 'esat') return 'esat';
  if (
    subject === 'computer science' ||
    subject === 'math' ||
    subject === 'mathematics' ||
    subject === 'tmua' ||
    subject === 'investment banking'
  ) {
    return 'tmua';
  }
  if (subject === 'management consulting' || subject === 'tara') return 'tara';

  return null;
}

export const KNOWN_SUITE_EXAM_NAMES = new Set([
  ...EXAM_PRACTICE_KEYS,
  'custom_quiz_generator',
  'personal_statement',
  'cv',
  'cover_letter',
  'interview_simulator',
  'flash_cards',
  'medical_interviews',
]);

/** Live voice/video interview toolTypes — not quiz/topic practice. */
const LIVE_INTERVIEW_TOOL_TYPES = new Set([
  'medicine_mmi_interview',
  'medicine_panel_interview',
  'oxbridge_interview',
  'interview_simulator',
  'investment_banking_interview',
  'private_equity_interview',
  'engineering_interview',
  'computer_science_interview',
  'law_interview',
  'operations_fs_interview',
  'scholarship_interview',
]);

/** Canonical exam_name → direct launch path (never /apps-store/:id). */
export const LAUNCH_BY_EXAM = {
  medicine_mmi_interview: '/website-interview?toolType=medicine_mmi_interview',
  medicine_panel_interview: '/website-interview?toolType=medicine_panel_interview',
  oxbridge_interview: '/website-interview?toolType=oxbridge_interview',
  interview_simulator: '/website-interview?toolType=interview_simulator',
  investment_banking_interview: '/website-interview?toolType=investment_banking_interview',
  private_equity_interview: '/website-interview?toolType=private_equity_interview',
  engineering_interview: '/website-interview?toolType=engineering_interview',
  computer_science_interview: '/website-interview?toolType=computer_science_interview',
  law_interview: '/website-interview?toolType=law_interview',
  operations_fs_interview: '/website-interview?toolType=operations_fs_interview',
  scholarship_interview: '/website-interview?toolType=scholarship_interview',
  // Live interview workspaces (interview-papers corpus), not the older
  // /website-interview service above. These MUST be here rather than
  // relying on the tile's own `path`: resolveAppLaunchTarget checks the
  // title against this map before it ever looks at `path`, and
  // "Medicine Interviews" normalises onto the medical_interviews alias
  // below - so the tile silently opened the MEDINT quiz bank instead.
  live_interview_medicine: '/interviews/medicine',
  live_interview_dentistry: '/interviews/dentistry',
  live_interview_oxbridge: '/interviews/oxbridge',
  // Quiz bank (MEDINT topics) — must not fall through to live interview
  medical_interviews: '/practice/interviews/Medical%20Interviews',
  lnat: '/practice/lnat',
  tmua: '/practice/tmua',
  ucat: '/practice/ucat',
  tsa: '/practice/tsa',
  esat: '/practice/esat',
  tara: '/practice/tara',
  lsat: '/practice/lsat',
  step: '/practice/step',
  logical_reasoning: '/website-reasoning-test?type=logical',
  pattern_recognition: '/website-reasoning-test?type=pattern',
  numerical_reasoning: '/website-reasoning-test?type=numerical',
  verbal_reasoning: '/website-reasoning-test?type=verbal',
  logical: '/website-reasoning-test?type=logical',
  pattern: '/website-reasoning-test?type=pattern',
  numerical: '/website-reasoning-test?type=numerical',
  verbal: '/website-reasoning-test?type=verbal',
  cv: '/application-builder/form/cv',
  cover_letter: '/application-builder/form/cover_letter',
  personal_statement: '/application-builder/form/personal_statement',
  custom_quiz_generator: '/custom-quiz-generator',
  notes: '/notes',
  uai: '/chat',
  flash_cards: '/flash-cards',
  flashcards: '/flash-cards',
  my_path: MY_PATH_URL,
};

const EXAM_ALIASES = {
  'logical reasoning': 'logical_reasoning',
  'pattern recognition': 'pattern_recognition',
  'numerical reasoning': 'numerical_reasoning',
  'verbal reasoning': 'verbal_reasoning',
  mmi: 'medicine_mmi_interview',
  panel: 'medicine_panel_interview',
  'mmi interview': 'medicine_mmi_interview',
  'panel interview': 'medicine_panel_interview',
  'medical interviews': 'medical_interviews',
  'medicine interviews': 'medical_interviews',
  'oxbridge interview': 'oxbridge_interview',
  'interview simulator': 'interview_simulator',
  'investment banking interview': 'investment_banking_interview',
  'private equity interview': 'private_equity_interview',
  'engineering interview': 'engineering_interview',
  'computer science interview': 'computer_science_interview',
  'law interview': 'law_interview',
  'operations interview (fs)': 'operations_fs_interview',
  'operations interview (financial services)': 'operations_fs_interview',
  'scholarship interview': 'scholarship_interview',
  'cv builder': 'cv',
  'cover letter builder': 'cover_letter',
  'personal statement builder': 'personal_statement',
  'flash cards': 'flash_cards',
  'custom quiz generator': 'custom_quiz_generator',
  'custom quiz': 'custom_quiz_generator',
  'lnat practice': 'lnat',
  'tmua practice': 'tmua',
  'ucat practice': 'ucat',
  'esat practice': 'esat',
  'tara practice': 'tara',
};

function normalizeKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function toExamSlug(value) {
  const spaced = normalizeKey(value);
  if (!spaced) return '';
  if (EXAM_ALIASES[spaced]) return EXAM_ALIASES[spaced];
  return spaced.replace(/\s+/g, '_');
}

/** Full standalone reasoning suite slugs (not short aliases like "logical"). */
const STANDALONE_REASONING_SLUGS = new Set([
  'logical_reasoning',
  'pattern_recognition',
  'numerical_reasoning',
  'verbal_reasoning',
]);

function inferReasoningLaunch(app) {
  if (app.has_logical_reasoning) return LAUNCH_BY_EXAM.logical_reasoning;
  if (app.has_pattern_recognition) return LAUNCH_BY_EXAM.pattern_recognition;
  if (app.has_numerical_reasoning) return LAUNCH_BY_EXAM.numerical_reasoning;
  if (app.has_verbal_reasoning) return LAUNCH_BY_EXAM.verbal_reasoning;
  return null;
}

/**
 * True for the four standalone Reasoning Apps (Logical / Pattern / Numerical / Verbal).
 * Case-study experiences that also include reasoning stay false.
 */
export function isStandaloneReasoningApp(app) {
  if (!app || app.case_study_id) return false;
  const slug = toExamSlug(app.exam_name) || toExamSlug(app.title);
  return STANDALONE_REASONING_SLUGS.has(slug);
}

/** Direct /website-reasoning-test?type=… launch for standalone reasoning apps only. */
export function resolveStandaloneReasoningLaunch(app) {
  if (!isStandaloneReasoningApp(app)) return null;
  const slug = toExamSlug(app.exam_name) || toExamSlug(app.title);
  return (slug && LAUNCH_BY_EXAM[slug]) || null;
}

/**
 * Apps retired from the UI for everyone (DECISIONS.md "Retired from the UI").
 * They have no launch target, whatever key, title or path the tile carries.
 */
export const RETIRED_APP_SLUGS = new Set([
  'library',
  'smart_library',
  'smart_reference_library',
  'supercurricular_books',
  'super_curricular_books',
  'supper_curricular_books',
  'caius_explore',
  'code_lab',
  'financial_modeling_lab',
  'financial_modeling',
  'financial_model',
  'financial_modeller',
  'financial_modeler',
  'flowforge',
]);

const RETIRED_APP_PATHS = [
  '/library',
  '/supercurricular-books',
  '/caius-explore',
  '/code-lab',
  '/financial-modeling-lab',
  '/flowforge',
];

/** True when this tile is one of the retired apps (by key, title or path). */
export function isRetiredApp(app) {
  if (!app) return false;
  if (RETIRED_APP_SLUGS.has(toExamSlug(app.exam_name)) || RETIRED_APP_SLUGS.has(toExamSlug(app.title))) {
    return true;
  }
  const path = String(app.path || '').toLowerCase().split('?')[0].replace(/\/+$/, '');
  return RETIRED_APP_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

/** Resolve a direct, working route for an app tile. Never returns /apps-store/:id. */
export function resolveAppLaunchTarget(app) {
  if (!app) return null;
  if (isRetiredApp(app)) return null;

  // Standalone reasoning apps must never fall through to /apps-store/.../experience
  const reasoningDirect = resolveStandaloneReasoningLaunch(app);
  if (reasoningDirect) return reasoningDirect;

  const examSlug = toExamSlug(app.exam_name);
  const titleSlug = toExamSlug(app.title);

  // Prefer canonical launch map (keeps ?type= on reasoning URLs)
  if (examSlug && LAUNCH_BY_EXAM[examSlug]) return LAUNCH_BY_EXAM[examSlug];
  if (titleSlug && LAUNCH_BY_EXAM[titleSlug]) return LAUNCH_BY_EXAM[titleSlug];

  // Flag-based fallback only when not a case-study experience
  if (!app.case_study_id) {
    const fromFlags = inferReasoningLaunch(app);
    if (fromFlags) return fromFlags;
  }

  if (
    app.path &&
    String(app.path).startsWith('/') &&
    !String(app.path).includes('/apps-store/') &&
    !String(app.path).includes('/store/')
  ) {
    return app.path;
  }

  // Only live interview tool apps — not quiz banks like "Medical Interviews"
  if (LIVE_INTERVIEW_TOOL_TYPES.has(examSlug) || LIVE_INTERVIEW_TOOL_TYPES.has(titleSlug)) {
    return `/website-interview?toolType=${encodeURIComponent(app.exam_name || examSlug)}`;
  }

  for (const ex of [...EXAM_PRACTICE_KEYS, 'tsa']) {
    if (examSlug === ex || titleSlug === ex || examSlug.startsWith(`${ex}_`) || titleSlug.includes(ex)) {
      return `/practice/${ex}`;
    }
  }

  return null;
}

/** Navigate to a launch target, preserving query strings (?type=, ?toolType=). */
export function navigateAppLaunch(navigate, target, options) {
  if (!navigate || !target) return;
  const raw = String(target);
  const q = raw.indexOf('?');
  const navOpts = options && typeof options === 'object' ? options : undefined;
  if (q === -1) {
    navigate(raw, navOpts);
    return;
  }
  navigate({ pathname: raw.slice(0, q), search: raw.slice(q) }, navOpts);
}

/** @deprecated use resolveAppLaunchTarget */
export const resolveAllAppsLaunchTarget = resolveAppLaunchTarget;

/**
 * Whether an app tile is allowed on a given exam portal.
 * hostExam null (edu.ivori.ai / apex) → allow all non-case apps.
 */
export function isAppAllowedOnHostTrack(app, hostExam) {
  if (!app) return false;
  if (!hostExam) return true;
  const allowed = TRACK_ALLOWED_EXAM_NAMES[hostExam];
  if (!allowed) return true;
  const slug = toExamSlug(app.exam_name) || toExamSlug(app.title);
  if (!slug) return false;
  return allowed.includes(slug);
}
